'use client';
import { FirebaseApp } from 'firebase/app';
import { Auth } from 'firebase/auth';
import { Firestore } from 'firebase/firestore';
import { FirebaseStorage } from 'firebase/storage';
import { createContext, useContext, useMemo } from 'react';

export interface FirebaseContextValue {
  app: FirebaseApp | null;
  firestore: Firestore | null;
  auth: Auth | null;
  storage: FirebaseStorage | null;
}

const FirebaseContext = createContext<FirebaseContextValue | null>(null);

export interface FirebaseProviderProps extends FirebaseContextValue {
  children: React.ReactNode;
}

export function FirebaseProvider({
  app,
  firestore,
  auth,
  storage,
  children,
}: FirebaseProviderProps) {
  const value = useMemo(
    () => ({
      app,
      firestore,
      auth,
      storage,
    }),
    [app, firestore, auth, storage]
  );
  return (
    <FirebaseContext.Provider value={value}>{children}</FirebaseContext.Provider>
  );
}

export const useFirebase = () => {
  return useContext(FirebaseContext);
};

export const useFirebaseApp = () => {
  return useFirebase()?.app ?? null;
};

export const useFirestore = () => {
  return useFirebase()?.firestore ?? null;
};

export const useAuth = () => {
  return useFirebase()?.auth ?? null;
};

export const useStorage = () => {
    return useFirebase()?.storage ?? null;
}
